/**
 * Sorting a department's drafts on the AOP Approvals tab (Anna, Nov 5 2026: Operations has dozens
 * of drafts, and the reviewers want them by unit, start, manager, priority or cost).
 *
 * Per department, in the browser, and only once someone clicks a header: until then the drafts keep
 * the order the proxy sends. Pure, so the order is tested without rendering.
 *
 * - An empty value (no unit, no start, no figure …) is LAST in both directions: a blank is not the
 *   smallest or the largest value, and sorting by cost must not open with the drafts that have none.
 * - Priority is AAA, A, B, C (`PRIORITY_RATINGS` on the proxy), highest first when ascending; a
 *   value outside that list sorts after it, alphabetically.
 * - Ties fall back to the title, and Unit to the start date first — the reading order Operations
 *   asked for (unit, then when).
 */
import type { AopApprovalDraft } from '../../services/aopPlanningApi';

export type DraftSortKey = 'role' | 'unit' | 'start' | 'hiringManager' | 'priority' | 'yearlyCost';
export type SortDirection = 'asc' | 'desc';
export interface DraftSort {
  key: DraftSortKey;
  direction: SortDirection;
}

const PRIORITY_ORDER = ['AAA', 'A', 'B', 'C'];

const collator = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

/** Compare two present values of one kind; numbers numerically, text by the German collator. */
function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

/** A priority as a sortable value: its rank in `PRIORITY_ORDER`, an unknown one after all of them. */
function priorityValue(priority: string | null): string | number | null {
  if (!priority) return null;
  const rank = PRIORITY_ORDER.indexOf(priority.trim().toUpperCase());
  return rank === -1 ? `${PRIORITY_ORDER.length}${priority}` : rank;
}

/** The value one key sorts a draft by; null for an empty cell. */
function valueOf(draft: AopApprovalDraft, key: DraftSortKey): string | number | null {
  switch (key) {
    case 'role':
      return draft.title || null;
    case 'unit':
      return draft.unit || null;
    case 'start':
      return draft.plannedStartDate || null;
    case 'hiringManager':
      return draft.hiringManager || null;
    case 'priority':
      return priorityValue(draft.priority);
    case 'yearlyCost':
      return draft.yearlyCost ?? null;
  }
}

/** Compare by one key in one direction, an empty value last whatever the direction. */
function compareBy(a: AopApprovalDraft, b: AopApprovalDraft, key: DraftSortKey, direction: SortDirection): number {
  const av = valueOf(a, key);
  const bv = valueOf(b, key);
  if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
  const order = compareValues(av, bv);
  return direction === 'asc' ? order : -order;
}

/** The drafts in `sort` order, as a new array; the proxy's order when `sort` is null. */
export function sortDrafts(drafts: AopApprovalDraft[], sort: DraftSort | null): AopApprovalDraft[] {
  if (!sort) return drafts;
  const tieBreaks: DraftSortKey[] = sort.key === 'unit' ? ['start', 'role'] : sort.key === 'role' ? [] : ['role'];
  return [...drafts].sort(
    (a, b) =>
      compareBy(a, b, sort.key, sort.direction) ||
      tieBreaks.reduce((result, key) => result || compareBy(a, b, key, 'asc'), 0)
  );
}

/** The sort after clicking `key`'s header: ascending first, then the other way round. */
export function nextSort(current: DraftSort | null, key: DraftSortKey): DraftSort {
  if (current?.key === key) return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  return { key, direction: 'asc' };
}
